import { formatDate } from "@/utils";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Badge, Icon } from "@tremor/react";
import { Input } from "@/components/ui/input";
import {
	CheckCircleIcon,
	ExclamationCircleIcon,
	TrashIcon,
	UserIcon,
	ShieldCheckIcon,
	PlayIcon,
	PauseIcon,
	KeyIcon,
} from "@heroicons/react/solid";
import { Clipboard, Loader2 } from "lucide-react";
import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { toastErrorUnlessHandled } from "@/hooks/useAxiosPrivate";

interface NbUser {
	id: number;
	email: string;
	role: string;
	is_active: boolean;
	created_at: string;
	is_registered?: boolean;
}

interface NbUsersTableProps {
	nbUsers: NbUser[];
	deleteNbUser: (id: number) => void;
	updateUserStatus: (id: number, isActive: boolean) => void;
	isLoading: boolean;
	resetUserPassword?: (id: number) => Promise<string | null>;
}

const NbUsersTable = ({
	nbUsers,
	deleteNbUser,
	updateUserStatus,
	isLoading,
	resetUserPassword,
}: NbUsersTableProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<NbUser | null>(null);
	const [resetLink, setResetLink] = useState<string | null>(null);
	const [isResetting, setIsResetting] = useState(false);

	const handleDeleteClick = (e: React.MouseEvent, userId: number) => {
		e.stopPropagation();
		deleteNbUser(userId);
	};

	const handleStatusToggle = (
		e: React.MouseEvent,
		userId: number,
		currentStatus: boolean,
	) => {
		e.stopPropagation();
		updateUserStatus(userId, !currentStatus);
	};

	const handleResetPassword = async (e: React.MouseEvent, user: NbUser) => {
		e.stopPropagation();
		setSelectedUser(user);
		setResetPasswordDialogOpen(true);
	};

	const confirmResetPassword = async () => {
		if (!selectedUser || !resetUserPassword) return;

		setIsResetting(true);
		try {
			const link = await resetUserPassword(selectedUser.id);
			if (link) {
				setResetLink(link);
			}
		} catch (error) {
			console.error("Failed to reset password:", error);
			toastErrorUnlessHandled(
				error,
				"Αποτυχία επαναφοράς κωδικού πρόσβασης",
			);
		} finally {
			setIsResetting(false);
		}
	};

	const copyToClipboard = () => {
		if (!resetLink) return;

		navigator.clipboard
			.writeText(resetLink)
			.then(() => toast.success("Ο σύνδεσμος αντιγράφηκε στο πρόχειρο"))
			.catch(() => toast.error("Δεν ήταν δυνατή η αντιγραφή του συνδέσμου"));
	};

	const closeResetDialog = () => {
		setResetPasswordDialogOpen(false);
		setSelectedUser(null);
		setResetLink(null);
	};

	// Filter users based on search term
	const filteredUsers = nbUsers.filter(
		(user) =>
			user.email.toLowerCase().includes(searchTerm.toLowerCase()) 
	);

	return (
		<Card className="col-span-8">
			<CardContent className="pt-4">
				<div className="mb-4">
					<Input
						placeholder="Αναζήτηση χρηστών..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="max-w-sm"
					/>
				</div>

				{isLoading ? (
					<div className="flex justify-center items-center h-40">
						<Loader2 className="h-8 w-8 animate-spin text-primary" />
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-[250px]">Email</TableHead>
								<TableHead className="w-[150px]">Ρόλος</TableHead>
								<TableHead className="w-[150px]">Κατάσταση</TableHead>
								<TableHead className="w-[200px]">
									Ημερομηνία Δημιουργίας
								</TableHead>
								<TableHead className="text-right">Ενέργειες</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredUsers.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5} className="h-24 text-center">
										Δεν βρέθηκαν χρήστες
									</TableCell>
								</TableRow>
							) : (
								filteredUsers.map((user) => (
									<TableRow key={user.id}>
										<TableCell className="font-medium">
											<div className="flex items-center space-x-2">
												{user.is_registered ? (
													<Icon
														icon={CheckCircleIcon}
														className="h-5 w-5 text-emerald-500"
														tooltip="Εγγραφή Ολοκληρωμένη"
													/>
												) : (
													<Icon
														icon={ExclamationCircleIcon}
														className="h-5 w-5 text-amber-500"
														tooltip="Εγγραφή Εκρεμμεί"
													/>
												)}
												<span>{user.email}</span>
											</div>
										</TableCell>
										<TableCell>
											<Badge
												color={user.role === "admin" ? "rose" : "blue"}
												icon={
													user.role === "admin" ? ShieldCheckIcon : UserIcon
												}
											>
												{user.role === "admin" ? "Διαχειριστής" : "Χρήστης"}
											</Badge>
										</TableCell>
										<TableCell>
											<Badge color={user.is_active ? "emerald" : "gray"}>
												{user.is_active ? "Ενεργός" : "Ανενεργός"}
											</Badge>
										</TableCell>
										<TableCell>
											{formatDate(user.created_at, {
												simple: true,
												withTime: true,
											})}
										</TableCell>
										<TableCell className="text-right">
											<div className="flex justify-end space-x-2">
												<button
													onClick={(e) =>
														handleStatusToggle(e, user.id, user.is_active)
													}
													className={`p-2 rounded hover:bg-slate-100 ${
														user.is_active
															? "text-amber-500"
															: "text-emerald-500"
													}`}
													title={
														user.is_active ? "Απενεργοποίηση" : "Ενεργοποίηση"
													}
												>
													{user.is_active ? (
														<PauseIcon className="h-5 w-5" />
													) : (
														<PlayIcon className="h-5 w-5" />
													)}
												</button>

												{resetUserPassword && (
													<button
														onClick={(e) => handleResetPassword(e, user)}
														className="p-2 rounded hover:bg-slate-100 text-blue-500"
														title="Επαναφορά Κωδικού"
													>
														<KeyIcon className="h-5 w-5" />
													</button>
												)}

												<button
													onClick={(e) => handleDeleteClick(e, user.id)}
													className="p-2 rounded hover:bg-slate-100 text-red-500"
													title="Διαγραφή"
												>
													<TrashIcon className="h-5 w-5" />
												</button>
											</div>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				)}

				{/* Reset Password Dialog */}
				<Dialog
					open={resetPasswordDialogOpen}
					onOpenChange={setResetPasswordDialogOpen}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>
								{resetLink
									? "Επαναφορά Κωδικού Επιτυχής"
									: "Επαναφορά Κωδικού Πρόσβασης"}
							</DialogTitle>
							<DialogDescription>
								{resetLink
									? "Παρακαλώ αντιγράψτε τον παρακάτω σύνδεσμο και στείλτε τον στον χρήστη"
									: `Είστε βέβαιοι ότι θέλετε να επαναφέρετε τον κωδικό για τον χρήστη ${selectedUser?.email}?`}
							</DialogDescription>
						</DialogHeader>

						{!resetLink ? (
							<div className="flex justify-end space-x-2 mt-4">
								<Button variant="outline" onClick={closeResetDialog}>
									Άκυρο
								</Button>
								<Button onClick={confirmResetPassword} disabled={isResetting}>
									{isResetting ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Επεξεργασία...
										</>
									) : (
										"Επαναφορά Κωδικού"
									)}
								</Button>
							</div>
						) : (
							<div className="space-y-4 mt-2">
								<div className="flex">
									<Input value={resetLink} readOnly className="pr-10" />
									<Button size="sm" className="ml-2" onClick={copyToClipboard}>
										<Clipboard className="h-4 w-4 mr-1" />
										Αντιγραφή
									</Button>
								</div>
								<div className="flex justify-end">
									<Button onClick={closeResetDialog}>Κλείσιμο</Button>
								</div>
							</div>
						)}
					</DialogContent>
				</Dialog>
			</CardContent>
		</Card>
	);
};

export default NbUsersTable;
